import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, VStack, useToast } from '@chakra-ui/react';

import FilterSection from './FilterSection';
import DataTable from './DataTable';
import Pagination from './Pagination';

import { getMarketplacesListApi } from 'features/marketplaces/marketplaces.api';
import { getStoresListApi } from 'features/stores/stores.api';
import { getSuppliersListApi } from 'features/suppliers/suppliers.api';
import {
  getListingsApi,
  deleteListingsByFilterApi,
} from 'features/listings/listings.api';

const ListingsTable = () => {
  const dispatch = useDispatch();
  const toast = useToast();

  const { list: marketplaceOptions, getMarketplacesListStatus } = useSelector(
    (state) => state.marketplaces,
  );
  const { list: storeOptions, getStoresListStatus } = useSelector(
    (state) => state.stores,
  );
  const { list: supplierOptions, getSuppliersListStatus } = useSelector(
    (state) => state.suppliers,
  );

  const {
    listings,
    totalListings,
    totalPages,
    getListingsStatus,
    deleteListingsByFilterStatus,
  } = useSelector((state) => state.listings);

  const [selectedItems, setSelectedItems] = useState([]);
  const [searchField, setSearchField] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [actualSearchTerm, setActualSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('dateCreated+');
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [filters, setFilters] = useState({
    priceMin: '',
    priceMax: '',
    quantityMin: '',
    quantityMax: '',
    marketplace: '',
    supplier: '',
    store: '',
    status: '',
  });
  const [deleteFilters, setDeleteFilters] = useState({
    store: '',
  });

  useEffect(() => {
    dispatch(getMarketplacesListApi());
    dispatch(getStoresListApi());
    dispatch(getSuppliersListApi());
  }, [dispatch]);

  useEffect(() => {
    const loadListings = () => {
      dispatch(
        getListingsApi({
          page: currentPage,
          limit: pageSize,
          search: actualSearchTerm,
          searchField,
          status: filters.status,
          priceMin: filters.priceMin ? parseFloat(filters.priceMin) : undefined,
          priceMax: filters.priceMax ? parseFloat(filters.priceMax) : undefined,
          quantityMin: filters.quantityMin ? parseInt(filters.quantityMin) : undefined,
          quantityMax: filters.quantityMax ? parseInt(filters.quantityMax) : undefined,
          marketplace: filters.marketplace,
          store: filters.store,
          supplier: filters.supplier,
          sortBy,
        }),
      );
    };

    loadListings();
  }, [dispatch, currentPage, pageSize, actualSearchTerm, searchField, filters, sortBy]);

  const handleFilterChange = (field, value) => {
    if (field === 'clear') {
      setFilters(value);
    } else {
      setFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleDeleteFilterChange = (field, value) => {
    if (field === 'clear') {
      setDeleteFilters(value);
    } else {
      setDeleteFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleDeleteByFilter = async () => {
    if (!deleteFilters.store) {
      toast({
        title: 'Error',
        description: 'Please select a store to delete listings from.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const result = await dispatch(
      deleteListingsByFilterApi({ storeId: deleteFilters.store }),
    );

    if (deleteListingsByFilterApi.fulfilled.match(result)) {
      // Reset delete filters and close section
      setDeleteFilters({ store: '' });
      setShowDeleteSection(false);

      // Reload listings to reflect changes (the reducer already handles state updates, but we reload for fresh data)
      dispatch(
        getListingsApi({
          page: currentPage,
          limit: pageSize,
          search: actualSearchTerm,
          searchField,
          status: filters.status,
          priceMin: filters.priceMin ? parseFloat(filters.priceMin) : undefined,
          priceMax: filters.priceMax ? parseFloat(filters.priceMax) : undefined,
          quantityMin: filters.quantityMin ? parseInt(filters.quantityMin) : undefined,
          quantityMax: filters.quantityMax ? parseInt(filters.quantityMax) : undefined,
          marketplace: filters.marketplace,
          store: filters.store,
          supplier: filters.supplier,
          sortBy,
        }),
      );
    }
  };

  useEffect(() => {
    if (searchQuery === '') {
      setActualSearchTerm('');
    }
  }, [searchQuery]);

  const handleSearch = () => {
    setActualSearchTerm(searchQuery);
    dispatch(
      getListingsApi({
        page: 1,
        limit: pageSize,
        search: searchQuery,
        searchField,
        status: filters.status,
        priceMin: filters.priceMin ? parseFloat(filters.priceMin) : undefined,
        priceMax: filters.priceMax ? parseFloat(filters.priceMax) : undefined,
        quantityMin: filters.quantityMin ? parseInt(filters.quantityMin) : undefined,
        quantityMax: filters.quantityMax ? parseInt(filters.quantityMax) : undefined,
        marketplace: filters.marketplace,
        store: filters.store,
        supplier: filters.supplier,
        sortBy,
      }),
    );
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const isLoading = getListingsStatus === 'pending';
  const isDeleting = deleteListingsByFilterStatus === 'pending';

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        <FilterSection
          searchField={searchField}
          setSearchField={setSearchField}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          filters={filters}
          handleFilterChange={handleFilterChange}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          showDeleteSection={showDeleteSection}
          setShowDeleteSection={setShowDeleteSection}
          deleteFilters={deleteFilters}
          handleDeleteFilterChange={handleDeleteFilterChange}
          handleDeleteByFilter={handleDeleteByFilter}
          marketplaceOptions={marketplaceOptions}
          storeOptions={storeOptions}
          supplierOptions={supplierOptions}
          getMarketplacesListStatus={getMarketplacesListStatus}
          getStoresListStatus={getStoresListStatus}
          getSuppliersListStatus={getSuppliersListStatus}
          isDeleting={isDeleting}
        />

        <DataTable
          listings={listings}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          sortBy={sortBy}
          setSortBy={setSortBy}
          pageSize={pageSize}
          setPageSize={setPageSize}
          currentPage={currentPage}
          totalListings={totalListings}
          isLoading={isLoading}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageInput={pageInput}
          setPageInput={setPageInput}
          handlePageChange={handlePageChange}
          isLoading={isLoading}
          listings={listings}
        />
      </VStack>
    </Box>
  );
};

export default ListingsTable;
